import constants from "constants/constants";
import {
  listing_repeat_interval_types,
  webinar_event_type,
} from "schedule-v2/constants";
import { BATCH_MAP_TYPES } from "schedule-v2/Classes/Classes.constants";
import { date_fns_formats } from "utils/dateTimeUtils";
import { parse, addMinutes, isBefore, isAfter } from "date-fns";
import { YEAR_MONTH_DAY_TIME_FORMAT } from "features/Common/modules/DateTime/DateTime.constants";
import { getDayFromInterval } from "features/Common/modules/DateTime/utils/DateTime.utils";

// Utility function to remove the `expired` property
// Utility function to deep copy and remove the `expired` property
function sanitizeRequest(request) {
  const deepCopiedRequest = JSON.parse(JSON.stringify(request)); // Create a deep copy
  return deepCopiedRequest.map((item) => {
    delete item?.expired; // Remove the `expired` property bcz it was causing issues due to inconsistent data in it this comparison was failing
    return item;
  });
}

export function createBatchChangeMap({ newData, oldData }) {
  let batchChangeMap = [];

  const newRequestGroup = newData.request_groups || [];
  const oldRequestGroup = oldData.request_groups || [];

  const maxLength = Math.max(newRequestGroup.length, oldRequestGroup.length);

  for (let index = 0; index < maxLength; index++) {
    const newRequest = newRequestGroup[index];
    const oldRequest = oldRequestGroup[index];

    const mainValuesChanged =
      newData.course_end_magnitude !== oldData.course_end_magnitude ||
      newData.duration !== oldData.duration ||
      newData.course_end_choice !== oldData.course_end_choice ||
      newData.start_date !== oldData.start_date;

    if (newRequest && oldRequest) {
      // Sanitize requests to ignore `expired` property
      const sanitizedNewRequest = sanitizeRequest(newRequest);
      const sanitizedOldRequest = sanitizeRequest(oldRequest);

      if (
        JSON.stringify(sanitizedNewRequest) ===
        JSON.stringify(sanitizedOldRequest)
      ) {
        batchChangeMap.push({
          change: mainValuesChanged
            ? BATCH_MAP_TYPES.UPDATE
            : BATCH_MAP_TYPES.NO_CHANGE,
          class_booked: oldRequest[0].class_booked,
          class_uuid: oldRequest[0].class_group_uuid,
        });
      } else {
        batchChangeMap.push({
          change: BATCH_MAP_TYPES.UPDATE,
          class_booked: oldRequest[0]?.class_booked,
          class_uuid: oldRequest[0]?.class_group_uuid || undefined,
        });
      }
    } else if (newRequest && !oldRequest) {
      // New request exists, but old request does not (new batch)
      batchChangeMap.push({
        change: BATCH_MAP_TYPES.NEW_BATCH,
      });
    } else if (!newRequest && oldRequest) {
      // Old request exists, but new request does not (deleted batch)
      batchChangeMap.push({
        change: BATCH_MAP_TYPES.DELETED_BATCH,
        class_booked: oldRequest[0]?.class_booked,
        class_uuid: oldRequest[0]?.class_group_uuid || undefined,
      });
    }
  }

  return batchChangeMap;
}

export const checkWebinarTimeOverlap = (requestGroups = [[]], duration = 0) => {
  let overlapped = false;
  let reason = "";

  // Helper function to parse datetime
  const parseDateTime = (interval, startTime) => {
    return parse(
      `${interval} ${startTime}`,
      YEAR_MONTH_DAY_TIME_FORMAT,
      new Date()
    );
  };

  // Helper function to check interval overlap
  const checkOverlap = (start1, end1, start2, end2) => {
    return isBefore(start1, end2) && isAfter(end1, start2);
  };

  // Check within each request group
  for (let groupIndex = 0; groupIndex < requestGroups.length; groupIndex++) {
    const requestGroup = requestGroups[groupIndex];
    for (let index = 0; index < requestGroup.length; index++) {
      const request = requestGroup[index];
      const startTime1 = parseDateTime(request.interval, request.start_time);
      const endTime1 = addMinutes(startTime1, duration);

      for (let i = index + 1; i < requestGroup.length; i++) {
        const otherRequest = requestGroup[i];
        const startTime2 = parseDateTime(
          otherRequest.interval,
          otherRequest.start_time
        );
        const endTime2 = addMinutes(startTime2, duration);

        if (checkOverlap(startTime1, endTime1, startTime2, endTime2)) {
          overlapped = true;
          reason = `Time Overlap found within Group ${
            groupIndex + 1
          } between sessions starting at ${request.start_time} and ${
            otherRequest.start_time
          }`;
          break;
        }
      }
      if (overlapped) break;
    }
    if (overlapped) break;
  }

  // Check across request groups
  for (let i = 0; i < requestGroups.length; i++) {
    for (let j = i + 1; j < requestGroups.length; j++) {
      for (const req1 of requestGroups[i]) {
        for (const req2 of requestGroups[j]) {
          if (req1.interval === req2.interval) {
            const startTime1 = parseDateTime(req1.interval, req1.start_time);
            const endTime1 = addMinutes(startTime1, duration);

            const startTime2 = parseDateTime(req2.interval, req2.start_time);
            const endTime2 = addMinutes(startTime2, duration);

            if (checkOverlap(startTime1, endTime1, startTime2, endTime2)) {
              overlapped = true;
              reason = `Time Overlap found across Groups ${i + 1} and ${
                j + 1
              } between sessions starting at ${req1.start_time} and ${
                req2.start_time
              }`;
              break;
            }
          }
        }
        if (overlapped) break;
      }
      if (overlapped) break;
    }
    if (overlapped) break;
  }

  return { overlapped, reason };
};

export const checkWebinarTimeOverlapWeekly = (
  requestGroups = [[]],
  duration = 0
) => {
  const checkedDays = [];
  let overlapped = false;
  let reason = "";

  // Helper function to parse time (without date)
  const parseTime = (timeStr) => {
    return parse(timeStr, date_fns_formats.hour_min__AMPM, new Date());
  };

  // Helper function to check interval overlap
  const checkOverlap = (start1, end1, start2, end2) => {
    return isBefore(start1, end2) && isAfter(end1, start2);
  };

  // Check within each group
  for (let groupIndex = 0; groupIndex < requestGroups.length; groupIndex++) {
    const requestGroup = requestGroups[groupIndex];
    for (const request of requestGroup) {
      const day = request.interval;
      if (checkedDays.includes(day)) {
        continue;
      }

      checkedDays.push(day);

      const sameDaySlots = requestGroup.filter((req) => req.interval === day);

      if (sameDaySlots.length > 1) {
        const startTime1 = parseTime(sameDaySlots[0].start_time);
        const endTime1 = addMinutes(startTime1, duration);

        for (let index = 1; index < sameDaySlots.length; index++) {
          const req2 = sameDaySlots[index];
          const startTime2 = parseTime(req2.start_time);
          const endTime2 = addMinutes(startTime2, duration);

          if (checkOverlap(startTime1, endTime1, startTime2, endTime2)) {
            overlapped = true;
            reason = `Time Overlap found within Group ${
              groupIndex + 1
            } on ${getDayFromInterval(day)} between sessions starting at ${
              sameDaySlots[0].start_time
            } and ${req2.start_time}`;
            break;
          }
        }
      }
      if (overlapped) break;
    }
    if (overlapped) break;
  }

  // Check across groups
  for (let i = 0; i < requestGroups.length; i++) {
    for (let j = i + 1; j < requestGroups.length; j++) {
      for (const req1 of requestGroups[i]) {
        for (const req2 of requestGroups[j]) {
          if (req1.interval === req2.interval) {
            const startTime1 = parseTime(req1.start_time);
            const endTime1 = addMinutes(startTime1, duration);

            const startTime2 = parseTime(req2.start_time);
            const endTime2 = addMinutes(startTime2, duration);

            if (checkOverlap(startTime1, endTime1, startTime2, endTime2)) {
              overlapped = true;
              reason = `Time Overlap found across Groups ${i + 1} and ${
                j + 1
              } on ${getDayFromInterval(
                req1.interval
              )} between sessions starting at ${req1.start_time} and ${
                req2.start_time
              }`;
              break;
            }
          }
        }
        if (overlapped) break;
      }
      if (overlapped) break;
    }
    if (overlapped) break;
  }

  return { overlapped, reason };
};

export const getWebinarConstants = (listingData) => {
  const {
    metadata: {
      webinar_event_type: webinarEventType = webinar_event_type.ONE_DAY_EVENT,
    } = {},
    interval_type: intervalType,
    type: listingType,
  } = listingData || {};

  const isWeeklyRepeatedInterval =
    intervalType === listing_repeat_interval_types.weekly;
  const isWebinarMultiDayEvent =
    webinarEventType == webinar_event_type.MULTI_DAY_EVENT;
  const isMultiDayWebinarWeeklyRepeated =
    isWebinarMultiDayEvent && isWeeklyRepeatedInterval;
  const isSingleDayWebinar =
    listingType === constants.scheduleTypesId.webinar &&
    !isWebinarMultiDayEvent;

  return {
    isWeeklyRepeatedInterval,
    isWebinarMultiDayEvent,
    isMultiDayWebinarWeeklyRepeated,
    isSingleDayWebinar,
  };
};
